import { defineStore } from 'pinia'
import { api, refreshCsrfToken, setCsrfToken } from '@/lib/api'

// Auth via Devise session cookie (§5.1/§8.1) + 2FA TOTP obrigatório (§hardening).
//
// Sessão tem dois estágios: a senha valida e abre uma sessão "meio-autenticada"
// (otpPending=true); só o desafio TOTP libera `user`. Endpoints respondem 403
// `otp_required` enquanto pendente — fetchMe traduz isso em otpPending.
export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null, // { id, name, email, role, external, company }
    ready: false, // true depois do primeiro fetchMe (resolve o guard de rota)
    otpPending: false, // senha ok, falta o desafio TOTP
    otpEnabled: false, // usuário já fez o enrollment (false = setup com QR)
  }),
  getters: {
    isAuthenticated: state => !!state.user,
    isAdmin: state => state.user?.role === 'admin',
    // Perfil efetivo p/ nav role-aware: admin | agent | external
    profile: state => {
      if (!state.user) return 'agent'
      if (state.user.external) return 'external'
      return state.user.role
    },
  },
  actions: {
    async fetchMe() {
      // Sessão só existe pós-2FA. Reload no meio do desafio TOTP perde o estado
      // em memória e cai aqui como não-autenticado → guard manda pro /login.
      const res = await api('/api/me')
      this.user = res.ok ? (await res.json()).user : null
      this.otpPending = false
      this.ready = true
      return this.user
    },

    async login(email, password) {
      await refreshCsrfToken()
      const res = await api('/users/sign_in', {
        method: 'POST',
        body: { user: { email, password } },
      })
      if (!res.ok) {
        if (res.status === 429) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.error || 'Muitas tentativas. Aguarde um momento.')
        }
        const msg = res.status === 401 ? 'Credenciais inválidas.' : 'Falha no login.'
        throw new Error(msg)
      }
      const data = await res.json()
      setCsrfToken(data.csrf_token)
      // Senha aceita, mas ainda sem usuário — segue pro desafio TOTP.
      this.user = null
      this.otpPending = true
      this.otpEnabled = !!data.otp_enabled
    },

    // Carrega QR + secret pro enrollment (1º acesso).
    async fetchOtpSetup() {
      const res = await api('/users/otp/setup')
      if (!res.ok) throw new Error('Não foi possível iniciar a configuração do 2FA.')
      return res.json() // { secret, otp_auth_uri, qr_svg }
    },

    // Confirma o enrollment (POST enable) ou o desafio recorrente (POST verify).
    async submitOtp(code, { setup = false } = {}) {
      const path = setup ? '/users/otp/enable' : '/users/otp/verify'
      const res = await api(path, { method: 'POST', body: { code } })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Código inválido.')
      }
      const data = await res.json()
      setCsrfToken(data.csrf_token)
      this.user = data.user
      this.otpPending = false
      return this.user
    },

    async logout() {
      await api('/users/sign_out', { method: 'DELETE' })
      this.user = null
      this.otpPending = false
      this.otpEnabled = false
    },
  },
})
